/**
 * Strip terminal colour codes from text bound for the HTML report.
 *
 * Playwright formats assertion failures for a terminal, so the message a
 * reporter receives carries SGR escape sequences. The report rendered them
 * verbatim, which is why a failure panel read:
 *
 *   Error: ESC[2mexpect(ESC[22mESC[31mpageESC[39m...
 *
 * and the Playwright call log came out sprayed with `[2m` / `[22m`. The escape
 * character survives JSON and `textContent` untouched — only a terminal ever
 * consumed it.
 */

/** ESC (0x1B) and the single-byte CSI introducer (0x9B). */
const ESC = '\u001B';
const CSI = '\u009B';

/**
 * ANSI escape sequences, both the modern CSI form and the older two-byte one.
 *
 * Deliberately broader than colour codes alone: cursor moves and erase
 * sequences turn up in wrapped tool output too, and none of them mean anything
 * in HTML.
 */
const ANSI_PATTERN = new RegExp(
  `[${ESC}${CSI}][[\\]()#;?]*(?:\\d{1,4}(?:;\\d{0,4})*)?[0-9A-PR-TZcf-nqry=><]`,
  'g',
);

/**
 * Remove ANSI escape sequences from a string.
 *
 * Returns the input unchanged when there is nothing to strip. That fast path
 * matters: this runs on every captured step title in a large suite, and the
 * overwhelming majority contain no escapes at all.
 */
export function stripAnsi(text: string): string {
  if (!text) return text;
  if (text.indexOf(ESC) === -1 && text.indexOf(CSI) === -1) return text;
  return text.replace(ANSI_PATTERN, '');
}

/** Strip, tolerating null and undefined so call sites stay uncluttered. */
export function stripAnsiSafe(text: string | null | undefined): string | null {
  if (text === null || text === undefined) return null;
  return stripAnsi(text);
}
